import { BuildingId, PermissionId, Role, RoleId, User } from 'shared/models';
import { useTranslation } from 'react-i18next';
import { NoneProvided } from 'ui/shared/noneProvided';
import { Dictionary } from 'lodash';
import { Link } from 'react-router-dom';
import { useSession } from 'hooks';
import { Permission } from 'shared/permissions';
import { useSelector } from 'react-redux';
import { getRoles } from 'selectors/rolesSelectors';
import { VirtualTable } from 'ui/shared/virtualTable/VirtualTable';
import { useCallback } from 'react';

require('./UserList.css');

function userHasPermission(roles: Record<RoleId, Role>, user: User, permissionId: PermissionId): boolean {
    return user.roleIds && user.roleIds.some(roleId => {
        const role = roles[roleId];
        return role && role.permissions[permissionId];
    });
}

const emailColumn = ({ emailAddress }: User) => {
    return (
        <>{ emailAddress || <NoneProvided/> }</>
    );
};

const phoneNumColumn = ({ phoneNumber }: User) => {
    return (
        <>{ phoneNumber || <NoneProvided/> }</>
    );
};

export type Props = {
    buildingId: BuildingId | null;
    users: Dictionary<User>;
    order: string[];
    loading?: boolean;
    makeLink(userId: string): string;
    onClickUnassign?(id: string): () => void;
};

export const UserList = ({ buildingId, users, order, loading = false, makeLink, onClickUnassign }: Props) => {
    const { t } = useTranslation();
    const { hasPermission, user } = useSession();
    const userId = user && user.id;

    const roles = useSelector(getRoles);

    const userColumn = useCallback((rowData: User) => {
        const { id, displayName, login } = rowData;

        const showEdit = hasPermission(buildingId, Permission.EditUser) || id === userId;
        const url = makeLink(id);
        return (
            <>
                { userHasPermission(roles, rowData, Permission.AllOrganizations)
                    ? <i className="yellow star icon" title={ t("USERS.LABEL.ADMIN") }/>
                    : userHasPermission(roles, rowData, Permission.AllBuildings)
                        ? <i className="red star icon" title={ t("USERS.LABEL.ADMIN") }/>
                        : rowData.orgId
                            ? <i className="grey user icon" title={ t("USERS.LABEL.USER") }/>
                            : <i className="blue user icon" title={ t("USERS.LABEL.USER") }/>
                }
                { showEdit
                    ? <Link to={ url }>
                        { displayName || login }
                    </Link>
                    : displayName || login
                }
            </>
        );
    }, [ buildingId, userId, roles, makeLink, hasPermission, t ]);

    const unassignColumn = useCallback(({ id, displayName, login }: User) => {
        return (
            <div className='_right-align'>
                <button
                    className="ui tiny icon basic button"
                    onClick={ onClickUnassign!(id) }
                    title={ t('COMMAND.UNASSIGN_USER', { name: displayName || login }) }
                >
                    <i className="minus icon"/>
                </button>
            </div>
        );
    }, [ onClickUnassign, t ]);

    const columns = [
        { render: userColumn, name: t('USERS.COLUMN.NAME') },
        { render: emailColumn, name: t('USERS.COLUMN.EMAIL_ADDRESS') },
        { render: phoneNumColumn, name: t('USERS.COLUMN.PHONE_NUMBER') },
    ];

    if (hasPermission(buildingId, Permission.AssignUser) && onClickUnassign) {
        columns.push({ render: unassignColumn, name: '' });
    }

    return (
        <VirtualTable
            className="user-list"
            rows={ users }
            rowOrder={ order }
            cols={ columns }
            loading={ loading }
            columnSizes={ '30% 45% 15% 10%' }
            rowClassName={ user => user.enabled ? '' : '_disabled' }
        />
    );
};
